import type { IntegrationSettings } from '@agos/api-client/model'
import { tt } from '@agos/shared'

/**
 * `/settings-integrations` — Wolt and Bolt Food, and the breakers of four services that are neither.
 *
 * **FB-148.** The integration does not exist. `wolt` appears in exactly two files across both backends —
 * `RestaurantSettingsService` and `TenantIntegrationsService`, the read and write paths of this very screen.
 * There is no menu push, no order ingestion, no webhook, no scheduler. Twelve switches, two API-key fields
 * and two venue ids, and the only effect any of them has is the row they are stored in. `sales_channels`
 * holds `delivery`, `restaurant`, `bar`, `na_sekeru` and the 10 491 orders carry the first three — no order
 * has ever arrived from either platform. `woltLastSync` is an empty string because nothing has ever synced,
 * which the page renders as *Nikdy*: a truthful word for a mechanism nobody built.
 *
 * **FB-149.** The breaker panel is about other services. `GET /health/circuit-breakers` returns **resend**,
 * **tatrapay-api**, **tatra-banka-token** and **tatra-banka-api** — none of them Wolt or Bolt, on a page
 * about Wolt and Bolt. And `BREAKER_LABELS` names `fiskalpro-restauracia` and `fiskalpro-rozvoz`, which the
 * endpoint does not return, while `resend` and `tatrapay-api`, which it does, have no label and render as raw
 * keys. Two dead labels, two unlabelled breakers, and `tt(BREAKER_LABELS[cb.name] ?? cb.name)` puts the raw
 * key through the translation memory on the way out.
 *
 * **What legacy gets right here, and the port keeps.** The API key is never fetched — the response carries
 * `hasWoltApiKey` and nothing else — and the field is only sent when somebody types in it. That is the
 * pattern FB-138, FB-143 and FB-132 had to be argued into everywhere else, already correct on this page.
 */

export type Platform = 'wolt' | 'bolt' | 'foodora'

/**
 * The platforms, in the order the page draws them - owner item O19, §9.307.
 *
 * **The backend has the same list and it is the one that matters**:
 * `RestaurantSettingsService.PLATFORMS` feeds both the read path and the guard that stops an
 * unchanged form blanking a stored API key. §10A.106 is there because those two had been spelled
 * separately, so a platform added to one and not the other lost its key on the first save.
 */
export const PLATFORMS: readonly Platform[] = ['wolt', 'bolt', 'foodora']

const PLATFORM_NAMES: Record<Platform, string> = {
  wolt: 'Wolt',
  bolt: 'Bolt Food',
  foodora: 'Foodora',
}

export function platformName(platform: Platform): string {
  return PLATFORM_NAMES[platform]
}

/** The SaaS module key each platform lives under. Tier gating fails open when the tier is unknown. */
export function moduleKey(platform: Platform): string {
  return `settings_integrations.${platform}`
}

export interface PlatformForm {
  /** Empty means *leave the stored key alone*. Never populated from the server. */
  apiKey: string
  venueId: string
  active: boolean
  menuSync: boolean
  acceptOrders: boolean
}

export type IntegrationsForm = Record<Platform, PlatformForm>

export interface PlatformState {
  hasApiKey: boolean
  lastSync: string | null
}

const EMPTY_PLATFORM: PlatformForm = {
  apiKey: '',
  venueId: '',
  active: false,
  menuSync: false,
  acceptOrders: false,
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

// The keys are built from the platform name — `woltVenueId`, `boltActive`. That reads as a reason to
// keep an untyped map, and is not: `IntegrationSettings` names all twelve, so a template-literal key
// resolves to a union both platforms satisfy and a typo in one of these strings is now a compile error.
// `Partial` rather than the bare type because every read here already defends against a missing key,
// and the unit tests pin that by passing one field at a time.
function platformOf(settings: Partial<IntegrationSettings> | undefined, platform: Platform): PlatformForm {
  return {
    // Deliberately not read from the response: the response does not carry it, and a field that starts
    // empty cannot overwrite a stored credential by accident.
    apiKey: '',
    venueId: text(settings?.[`${platform}VenueId`]),
    active: settings?.[`${platform}Active`] === true,
    menuSync: settings?.[`${platform}MenuSync`] === true,
    acceptOrders: settings?.[`${platform}AcceptOrders`] === true,
  }
}

export function formOf(settings: Partial<IntegrationSettings> | undefined): IntegrationsForm {
  // Built from PLATFORMS rather than listed, so a platform added to the union cannot be left out of
  // the form and silently read as every-switch-off. `Object.fromEntries` loses the key type, hence
  // the assertion - the union is still what makes a typo above a compile error.
  return Object.fromEntries(
    PLATFORMS.map((platform) => [
      platform,
      settings === undefined ? { ...EMPTY_PLATFORM } : platformOf(settings, platform),
    ]),
  ) as IntegrationsForm
}

export function stateOf(
  settings: Partial<IntegrationSettings> | undefined,
  platform: Platform,
): PlatformState {
  // `wolt` to `hasWoltApiKey`. Derived rather than a ternary, which silently answered *Bolt* for
  // anything that was not Wolt - correct while there were two platforms and wrong the moment a third
  // existed, reporting Bolt's key state on Foodora's panel.
  const key = `has${platform.charAt(0).toUpperCase()}${platform.slice(1)}ApiKey` as
    | 'hasWoltApiKey'
    | 'hasBoltApiKey'
    | 'hasFoodoraApiKey'
  const sync = text(settings?.[`${platform}LastSync`]).trim()
  return { hasApiKey: settings?.[key] === true, lastSync: sync === '' ? null : sync }
}

// ─── FB-148: what any of it does ──────────────────────────────────────────────

/**
 * Whether the platform integration is implemented anywhere.
 *
 * `false`, and measured rather than assumed: `wolt` and `bolt` appear only in the settings read and write
 * paths of both backends. There is no code that pushes a menu, receives an order or handles a webhook.
 */
export const INTEGRATION_IMPLEMENTED = false

export function unimplementedNote(): string {
  return tt(
    'Prepojenie s Woltom, Bolt Foodom ani Foodorou nie je naprogramované - žiadna úloha neposiela menu ani neprijíma objednávky. Tieto polia sa uložia a nič viac.',
  )
}

export type PlatformStatus = 'not-implemented' | 'off' | 'no-key' | 'configured'

/**
 * What the switches add up to, if the integration existed.
 *
 * Reported anyway, because a franchise that fills this in is describing an intention and the page should read
 * back what it stored. What it must not do is imply the intention has an effect.
 */
export function statusOf(form: PlatformForm, state: PlatformState): PlatformStatus {
  if (!INTEGRATION_IMPLEMENTED) return 'not-implemented'
  if (!form.active) return 'off'
  if (!state.hasApiKey && form.apiKey.trim() === '') return 'no-key'
  return 'configured'
}

export function lastSyncLabel(state: PlatformState): string {
  // `''` from the server means never, and *never* is the honest word for a sync that was never built.
  return state.lastSync ?? tt('Nikdy')
}

// ─── FB-149: the breakers ─────────────────────────────────────────────────────

export type BreakerState = 'CLOSED' | 'OPEN' | 'HALF_OPEN'

export interface Breaker {
  name: string
  state: BreakerState
  failureCount: number
  successCount: number
  lastFailure: string | null
  nextRetryAt: string | null
}

export function breakersOf(payload: unknown): Breaker[] {
  const list = (payload as { breakers?: unknown } | undefined)?.breakers
  if (!Array.isArray(list)) return []
  return list.map((entry) => {
    const row = entry as Record<string, unknown>
    const state = row.state
    return {
      name: text(row.name),
      state: state === 'OPEN' || state === 'HALF_OPEN' ? state : 'CLOSED',
      failureCount: typeof row.failureCount === 'number' ? row.failureCount : 0,
      successCount: typeof row.successCount === 'number' ? row.successCount : 0,
      lastFailure: text(row.lastFailure).trim() === '' ? null : text(row.lastFailure),
      nextRetryAt: text(row.nextRetryAt).trim() === '' ? null : text(row.nextRetryAt),
    }
  })
}

/**
 * The four names the endpoint actually returns.
 *
 * Legacy's table labels two that it does not (`fiskalpro-restauracia`, `fiskalpro-rozvoz`) and misses two
 * that it does. A name with no label is shown as its key — the key is what a log line carries — and it is
 * **not** passed through `tt()`, because a service name is data.
 */
export function breakerLabel(name: string): string {
  /* §10A.197 names a gateway's breaker per franchise - `tatrapay-api:7`, and §10A.444's `gpwebpay-ws:7` - so the
     label is the service's, read off the part before the franchise id. */
  const service = name.split(':')[0] ?? name
  if (service === 'gpwebpay-ws') return tt('GP webpay - platby')
  if (service === 'tatrapay-api') return tt('TatraPay - platby')
  if (name === 'resend') return tt('E-maily (Resend)')
  if (name === 'tatra-banka-token') return tt('Tatra Banka - prihlásenie (OAuth)')
  if (name === 'tatra-banka-api') return tt('Tatra Banka - transakcie')
  return name
}

export function isLabelledBreaker(name: string): boolean {
  return breakerLabel(name) !== name
}

/** Whether a breaker belongs to anything this page configures. `false` for every one of them. */
export function breakerBelongsHere(name: string): boolean {
  return PLATFORMS.some((platform) => name.startsWith(platform))
}

export function breakerStateLabel(state: BreakerState): string {
  if (state === 'OPEN') return tt('nedostupné')
  if (state === 'HALF_OPEN') return tt('skúša sa')
  return tt('funguje')
}

export function breakerNote(breakers: readonly Breaker[]): string {
  const foreign = breakers.filter((breaker) => !breakerBelongsHere(breaker.name)).length
  if (breakers.length === 0) return tt('Zatiaľ sa nesledovala žiadna externá služba.')
  if (foreign === breakers.length) {
    return tt('Ani jedna z týchto služieb nepatrí k doručovacím platformám - sú to platby, banka a e-maily.')
  }
  return ''
}

// `openBreakers` was here and is deleted — §9.35, 2026-08-06. It filtered the breakers that are not
// CLOSED, for a summary the page does not need: it draws one row per service and puts
// `breakerStateLabel` on each — *funguje*, *nedostupné*, *skúša sa*. Four services, four rows, every
// state already on screen. A count above a list of four is a second way to read the same thing, and
// the row is the one that says *which*.

// ─── The save ─────────────────────────────────────────────────────────────────

/**
 * What to send.
 *
 * Eight keys always, plus a key per platform **only** when somebody typed one. Ported unchanged: legacy is
 * already right here, and it is the same rule `/settings-sms` had to be given.
 */
export function payloadOf(form: IntegrationsForm): Record<string, unknown> {
  const payload: Record<string, unknown> = {}
  for (const platform of PLATFORMS) {
    const one = form[platform]
    payload[`${platform}VenueId`] = one.venueId.trim()
    payload[`${platform}Active`] = one.active
    payload[`${platform}MenuSync`] = one.menuSync
    payload[`${platform}AcceptOrders`] = one.acceptOrders
    if (one.apiKey.trim() !== '') payload[`${platform}ApiKey`] = one.apiKey.trim()
  }
  return payload
}

/** A venue id is opaque, but an empty one with the integration switched on is worth saying. */
export function venueProblem(form: PlatformForm): 'missing' | null {
  return form.active && form.venueId.trim() === '' ? 'missing' : null
}
